import { useContext } from 'react'
import { Link, useOutletContext } from 'react-router-dom'
import { ArrowUpRight, Box, LayoutGrid } from 'lucide-react'
import { isPlatformUser, AuthContext } from '@retink/web-auth'
import { api } from '../lib/api'
import { useResource, Tile, Card, Badge, buttonVariants, EmptyState, ErrorState, LoadingState, PageHeader, cn } from '@retink/web-ui'
import type { TenantOutletContext, TenantProduct } from '../lib/tenant-context'
import { CONTROL_PRODUCTS } from '../shell/products'

interface Member { id: string; tenant_role: string; cc_role: string | null; membership_status: string }
interface Invite { id: string; email: string; tenant_role: string; expires_at: string }

export function TenantDashboard() {
  const { user } = useContext(AuthContext)
  const { tenantContext, tenantLoading, tenantError, reloadTenant } = useOutletContext<TenantOutletContext>()
  const canViewAdmin = isPlatformUser(user) || user?.orgRole === 'admin' || user?.tenantRole === 'admin'
  const readOnly = user?.platformRole === 'viewer'

  if (tenantLoading) return <LoadingState label="Loading tenant…" className="h-full" />
  if (tenantError) return <ErrorState message="Couldn’t load this tenant." onRetry={reloadTenant} className="h-full" />

  // This is the *tenant* dashboard, so "Role" describes the user's standing in
  // THIS tenant — tenant/org scope wins. A platform staffer with no tenant
  // membership is shown honestly as staff, not mislabelled as a tenant role.
  const roleLabel =
    user?.tenantRole === 'admin' ? 'Tenant admin'
    : user?.orgRole === 'admin' ? 'Org admin'
    : user?.tenantRole === 'user' ? 'Member'
    : user?.platformRole ? `Platform ${user.platformRole} (staff)`
    : 'Member'

  const tenant = tenantContext?.tenant ?? null
  const products = tenantContext?.products ?? []

  return (
    <div className="p-8">
      <PageHeader
        title={tenant?.name ?? 'Your tenant'}
        description={tenant && (
          <span className="font-mono">
            {tenant.slug} · {tenant.org_name} · {tenant.region}
          </span>
        )}
      />

      {/* The launcher — every member's way into the tenant's products. Tiles go
          live (clickable) as each app deploys and registers its hostname. */}
      <section className="mb-8">
        <h2 className="mb-3 text-sm font-medium">Apps</h2>
        {products.length === 0 ? (
          <Card>
            <EmptyState
              icon={LayoutGrid}
              title="No apps yet"
              description="Products appear here as they're enabled for this tenant."
            />
          </Card>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {products.map((p) => <AppTile key={p.productId} product={p} />)}
          </div>
        )}
      </section>

      {canViewAdmin && <TenantAdminPanel />}

      <Card className="p-5">
        <h2 className="text-sm font-medium mb-3">Account</h2>
        <dl className="text-sm grid grid-cols-[7rem_1fr] gap-y-1">
          <dt className="text-muted-foreground">Signed in</dt><dd>{user?.email}</dd>
          <dt className="text-muted-foreground">Role</dt>
          <dd>{roleLabel}</dd>
        </dl>
        <div className="mt-4 flex gap-3">
          {canViewAdmin && (
            <Link to="/admin/tenant/team" className={cn(buttonVariants())}>{readOnly ? 'View team' : 'Manage team'}</Link>
          )}
          <Link to="/account" className={cn(buttonVariants({ variant: 'secondary' }))}>Account settings</Link>
        </div>
      </Card>
    </div>
  )
}

// One product on the launcher. Live products cross the standalone-build seam with
// a full-page anchor; unavailable products remain visible as coming soon.
function AppTile({ product }: { product: TenantProduct }) {
  const configured = CONTROL_PRODUCTS[product.productId]
  const Icon = configured?.icon ?? Box
  const href = configured?.path
  const body = (
    <>
      <div className="flex items-start justify-between gap-2">
        {/* Neutral, not accent and not a scope cue: these mark WHICH APP, and a
            grid of them would spray a colour that's rationed to actions (accent)
            or reserved for "which scope am I in" (scope). */}
        <div className="flex size-9 items-center justify-center rounded-md bg-muted text-muted-foreground">
          <Icon className="size-5" />
        </div>
        {href
          ? <ArrowUpRight className="size-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          : <Badge>coming soon</Badge>}
      </div>
      <div className="mt-3 font-medium text-foreground">{product.displayName}</div>
      {product.description && (
        <div className="mt-0.5 text-[13px] text-muted-foreground">{product.description}</div>
      )}
    </>
  )
  const cardClass = 'group rounded-xl border border-border bg-card p-5 transition-colors duration-[120ms] ease-out hover:border-accent-border'
  return href ? (
    <Link to={href} className={cardClass}>
      {body}
    </Link>
  ) : (
    <Card className="p-5 opacity-80">{body}</Card>
  )
}

// Admin-only slice of the dashboard: live team stats. Kept in its own component
// so the (admin-gated) /api/settings/members fetch never fires for a plain member.
function TenantAdminPanel() {
  const { data } = useResource<{ members: Member[]; invites: Invite[] }>(() =>
    api.get<{ members: Member[]; pendingInvites: Invite[] }>('/api/settings/members')
      .then((m) => ({ members: m.members, invites: m.pendingInvites })), [])

  const members = data?.members ?? []
  const invites = data?.invites ?? []
  const activeMembers = members.filter((m) => m.membership_status === 'active').length
  const adminCount = members.filter((m) => m.tenant_role === 'admin').length
  const ccCount = members.filter((m) => m.cc_role).length

  return (
    <div className="grid grid-cols-4 gap-4 mb-8">
      <Tile label="Members" value={activeMembers} />
      <Tile label="Tenant admins" value={adminCount} />
      <Tile label="Call-centre" value={ccCount} />
      <Tile label="Pending invites" value={invites.length} />
    </div>
  )
}
